import { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { $isListNode, ListNode } from '@lexical/list';
import { $isHeadingNode } from '@lexical/rich-text';

import { Box } from '../../../../Box';
import { DropdownButton } from '../../../../DropdownButton';
import { Icon } from '../../../../Icon';
import { ToolbarButton } from '../common';
import { getSelectedNode } from '../../utils';

import {
  blockListTypeToBlockName,
  FormatTypeKeys,
  ListTypeKeys,
  useFormatElement,
  useIndentFormat,
  useList,
} from './hooks';

interface ListAndFormatPluginProps {
  align: boolean;
  list: boolean;
  indent: boolean;
}

export const ListAndFormatPlugin = ({
  align,
  list: listNode,
  indent: indentNode,
}: ListAndFormatPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [alignFormat, setAlignFormat] = useState<FormatTypeKeys>('left');
  const [blockListType, setBlockListType] = useState<ListTypeKeys>('paragraph');

  const [format, activeFormatIcon] = useFormatElement(editor, alignFormat);
  const [list, activeListIcon] = useList(editor, blockListType);
  const [indent, indentIcon] = useIndentFormat(editor);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      const currentAlignFormat = parent?.getFormatType();

      setAlignFormat(() => (currentAlignFormat as FormatTypeKeys) || 'left');

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parentElem = e.getParent();
              return parentElem !== null && $isRootOrShadowRoot(parentElem);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();

          setBlockListType(() => type as ListTypeKeys);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockListTypeToBlockName) {
            setBlockListType(() => type as ListTypeKeys);
          } else {
            setBlockListType(() => 'paragraph');
          }
        }
      }
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateToolbar]);

  return (
    <Box display="flex" alignItems="center">
      {align && (
        <DropdownButton
          menuItems={format}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Выравнивание">
              <Box display="flex" alignItems="center" gap="4">
                {activeFormatIcon}
                <Icon name="dropdown" size="16" color="text.light.primary" />
              </Box>
            </ToolbarButton>
          )}
        />
      )}

      {listNode && (
        <DropdownButton
          menuItems={list}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Список">
              <Box display="flex" alignItems="center" gap="4">
                {activeListIcon}
                <Icon name="dropdown" size="16" color="text.light.primary" />
              </Box>
            </ToolbarButton>
          )}
        />
      )}

      {indentNode && (
        <DropdownButton
          menuItems={indent}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Отступ">
              <Box display="flex" alignItems="center" gap="4">
                {indentIcon}
                <Icon name="dropdown" size="16" color="text.light.primary" />
              </Box>
            </ToolbarButton>
          )}
        />
      )}
    </Box>
  );
};
